import React, { useState } from "react";
import { Alert, Button, Input, Table, Tag, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import rewardProgramApi from "../../../api/rewardProgramApi";
import RewardShopSearchPicker from "./reward-shop-search-picker";

const CAMP_LABELS = { shopee: "Shopee", lazada: "Lazada", tiktok: "TikTok" };
const CAMP_COLORS = { shopee: "orange", lazada: "blue", tiktok: "magenta" };
// Giới hạn mềm: config là 1 cột JSON, list quá dài làm form nặng và khó rà soát.
const MAX_ENTRIES = 200;

/**
 * reward-link-list-field — ô cấu hình "chỉ thưởng shop/sản phẩm này".
 *
 * Admin dán LINK (mỗi dòng 1 link) → BE tách id (resolve-links) → bảng bên dưới.
 * Value lưu vào config: [{ campId, shopId|productId, name, url }].
 *
 * Cột ID và Tên sửa TAY được — đường thoát khi sàn đổi HTML làm parser chết,
 * admin vẫn mở được đợt mà không phải chờ deploy.
 */
function RewardLinkListField({ field, value, onChange }) {
    const entity = field.entity === "shop" ? "shop" : "product";
    const idField = entity === "shop" ? "shopId" : "productId";
    const rows = Array.isArray(value) ? value : [];
    const [raw, setRaw] = useState("");
    const [loading, setLoading] = useState(false);

    // Dòng đang tra tên (theo index) — chặn bấm dồn và cho biết đang chạy.
    const [lookingUp, setLookingUp] = useState(null);

    const emit = (next) => onChange(field.key, next);

    /**
     * Tra TÊN + ẢNH của 1 dòng từ id (hoặc link đã lưu).
     * @param {boolean} force true = bấm nút (ghi đè tên cũ); false = tự chạy khi
     *        rời ô id, chỉ điền khi tên còn trống — không giẫm lên ghi chú admin gõ.
     */
    const lookupRow = async (index, force) => {
        const row = rows[index];
        const id = row?.[idField];
        if (!row || (!id && !row.url)) return;
        if (!force && row.name) return;
        setLookingUp(index);
        try {
            const { data } = await rewardProgramApi.lookupItem({
                campId: row.campId, entity, productId: id || undefined, url: row.url || undefined,
            });
            if (!data.name && !data.image) {
                if (force) message.warning("Không tra được tên — gõ tay vào ô ghi chú");
                return;
            }
            // shopId: Shopee cần cả shopId + itemId mới dựng được URL chính tắc.
            emit(rows.map((r, i) => (i === index
                ? { ...r, name: data.name || r.name, image: data.image || r.image, ...(data.shopId ? { shopId: data.shopId } : {}) }
                : r)));
        } catch (err) {
            if (force) message.error(err?.response?.data?.message || "Không tra được tên sản phẩm");
        } finally {
            setLookingUp(null);
        }
    };

    const handleResolve = async () => {
        const links = raw.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean);
        if (!links.length) return message.warning("Dán ít nhất 1 link");
        if (links.length > 20) return message.warning("Tối đa 20 link mỗi lần phân tích");

        setLoading(true);
        try {
            const { data } = await rewardProgramApi.resolveLinks(links, entity);
            const next = [...rows];
            // Bỏ trùng theo campId + id (dán 2 link cùng shop là chuyện thường).
            const seen = new Set(next.map((r) => `${r.campId}:${r[idField]}`));
            let added = 0;
            for (const r of data.results || []) {
                if (!r.ok || !r[idField]) {
                    message.error(`${r.error || "Không phân tích được"}: ${r.url}`, 6);
                    continue;
                }
                const k = `${r.campId}:${r[idField]}`;
                if (seen.has(k)) continue;
                seen.add(k);
                // name/image do BE tra sẵn (addlivetag / og-meta) — app dùng lại để
                // dựng block "áp dụng cho", nên phải giữ, không chỉ để admin nhìn.
                next.push({
                    campId: r.campId, [idField]: String(r[idField]),
                    name: r.name || null, image: r.image || null,
                    // Entry SẢN PHẨM Shopee giữ kèm shopId → BE dựng URL chính tắc.
                    ...(entity === "product" && r.shopId ? { shopId: String(r.shopId) } : {}),
                    // url gửi lên chỉ là dự phòng khi không dựng được URL từ id.
                    url: r.url,
                });
                added++;
            }
            if (next.length > MAX_ENTRIES) return message.error(`Tối đa ${MAX_ENTRIES} dòng mỗi danh sách`);
            if (added) {
                emit(next);
                setRaw("");
                message.success(`Đã thêm ${added} dòng`);
            }
        } catch (err) {
            message.error(err?.response?.data?.message || "Không phân tích được link");
        } finally {
            setLoading(false);
        }
    };

    // Thêm 1 shop chọn từ ô tìm kiếm (đường DUY NHẤT cho TikTok — link không ra mã shop).
    const handlePickShop = (entry) => {
        const k = `${entry.campId}:${entry.shopId || entry.name}`;
        if (rows.some((r) => `${r.campId}:${r.shopId || r.name}` === k)) return message.info("Shop đã có trong danh sách");
        emit([...rows, entry]);
        message.success(`Đã thêm ${entry.name || entry.shopId}`);
    };

    const patchRow = (index, key, val) => {
        const next = rows.map((r, i) => (i === index ? { ...r, [key]: val } : r));
        emit(next);
    };

    const columns = [
        {
            title: "Sàn", dataIndex: "campId", width: 90,
            render: (c) => <Tag color={CAMP_COLORS[c]}>{CAMP_LABELS[c] || c}</Tag>,
        },
        {
            title: entity === "shop" ? "Shop ID" : "Sản phẩm ID", width: 190,
            render: (_, r, i) => (
                <Input
                    size="small" value={r[idField] || ""}
                    onChange={(e) => patchRow(i, idField, e.target.value.trim())}
                    // Gõ xong id → tự tra tên. Chạy lúc BLUR chứ không theo từng ký tự:
                    // onChange sẽ bắn request cho mỗi chữ số admin gõ.
                    onBlur={() => lookupRow(i, false)}
                    onPressEnter={() => lookupRow(i, false)}
                />
            ),
        },
        {
            title: "Ảnh", width: 56,
            render: (_, r) => (r.image
                ? <img src={r.image} alt="" className="w-10 h-10 object-cover rounded border" />
                : <span className="text-content-3">—</span>),
        },
        {
            title: "Tên (ghi chú)",
            render: (_, r, i) => (
                <Input size="small" placeholder="tên để nhận diện" value={r.name || ""} onChange={(e) => patchRow(i, "name", e.target.value)} />
            ),
        },
        // CỐ Ý không có cột link: link admin dán chỉ là đầu vào để bóc id. URL đích
        // được BE sinh lại từ id mỗi lần lưu (product-url-from-ids), nên link lưu
        // trong config luôn sạch — cho admin sửa tay chỉ tổ tạo link rác.
        {
            title: "", width: 88,
            render: (_, __, i) => (
                <div className="flex gap-1">
                    <Button
                        size="small" title="Tra lại tên + ảnh từ id"
                        loading={lookingUp === i}
                        icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                        onClick={() => lookupRow(i, true)}
                    />
                    <Button size="small" danger icon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => emit(rows.filter((_, idx) => idx !== i))} />
                </div>
            ),
        },
    ];

    return (
        <div className="w-full space-y-2">
            <div className="flex gap-2">
                <Input.TextArea
                    rows={2} value={raw} onChange={(e) => setRaw(e.target.value)}
                    placeholder={entity === "shop"
                        ? "Dán link SẢN PHẨM của shop (mỗi dòng 1 link) — hệ thống tự lấy shop"
                        : "Dán link sản phẩm (mỗi dòng 1 link)"}
                />
                <Button type="primary" loading={loading} onClick={handleResolve} icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}>
                    Phân tích
                </Button>
            </div>

            {entity === "shop" && <RewardShopSearchPicker onPick={handlePickShop} />}

            {rows.length > 0 && (
                <>
                    {/* scroll x: bảng 6 cột, màn hẹp thì cho cuộn ngang thay vì bóp
                        cột Tên còn vài chục px (tên sản phẩm sàn dài cả dòng). */}
                    <Table size="small" pagination={false} rowKey={(r, i) => `${r.campId}-${r[idField]}-${i}`}
                        dataSource={rows} columns={columns} scroll={{ x: "max-content" }} />
                    <p className="text-xs text-content-3">
                        <b>ID</b> là thứ duy nhất được lưu để xét thưởng. Link bạn dán chỉ dùng để bóc ID ra —
                        link mua của user do hệ thống tự sinh lại từ ID và gắn mã riêng từng người, nên không
                        cần (và không nên) lưu link gốc.
                    </p>
                </>
            )}

            {field.hint && <p className="text-xs text-content-3">{field.hint}</p>}

            {/* Cảnh báo bắt buộc — chống khiếu nại "sao đơn tôi không được thưởng". */}
            {rows.length > 0 && (
                <Alert type="warning" showIcon
                    message="Đang BẬT giới hạn: chỉ đơn có ít nhất 1 shop/sản phẩm trong danh sách mới được thưởng. Đơn còn lại của người tham gia sẽ trượt kèm lý do."
                />
            )}
            {entity === "product" && rows.length > 0 && (
                <Alert type="error" showIcon
                    message="Đơn Shopee mua TRƯỚC khi bật tính năng chưa có id sản phẩm nên sẽ trượt với lý do 'Thiếu dữ liệu SP'. Chạy backfill (crawl lại cửa sổ ngày) trước khi mở đợt."
                />
            )}
            {entity === "shop" && (
                <p className="text-xs text-content-3">
                    Shop TikTok không lấy được từ link — phải gõ tên vào ô tìm kiếm ở trên.
                    Đơn TikTok "mua qua link" (RioHub) chỉ có TÊN shop nên khớp theo tên;
                    2 shop trùng tên sẽ tính chung.
                </p>
            )}
        </div>
    );
}

export default RewardLinkListField;
